import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import SlideOver from './SlideOver';
import { CO_MANUAL_TYPES, CO_COST_IMPACTS, type CoManualType, type CoCostImpact } from '@blueprint/shared';

type EstimationType = 'STORY_POINTS' | 'EU_RU' | 'BOTH';

interface ChangeOrderOption {
  id: string;
  label: string;
  description: string;
  recommended: boolean;
}

interface Props {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
  estimationType?: EstimationType;
}

// Raised by hand, so only the manual types (the create route accepts nothing else).
const CO_TYPE_LABEL: Record<CoManualType, string> = {
  SPIKE: 'Spike — unplanned technical work',
  COMPLEXITY_INCREASE: 'Complexity Increase — scope grew',
  REQUIREMENTS_GAP: 'Requirements Gap — missing requirement',
  MANUAL: 'Manual — other reason',
};
const CO_TYPES = CO_MANUAL_TYPES.map(value => ({ value, label: CO_TYPE_LABEL[value] }));

const COST_LABEL: Record<CoCostImpact, string> = {
  NONE: 'No cost impact',
  INCLUDED: 'Included in sprint budget',
  ADDITIONAL: 'Additional charge',
};
const COST_OPTIONS = CO_COST_IMPACTS.map(value => ({ value, label: COST_LABEL[value] }));

const DEFAULT_OPTIONS: ChangeOrderOption[] = [
  { id: 'approve', label: 'Approve', description: 'Include in current sprint', recommended: true },
  { id: 'defer', label: 'Defer to next sprint', description: 'Defer this work to the next sprint cycle', recommended: false },
];

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', background: 'var(--surface-alt)',
  color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: 'var(--font-body)', fontSize: 12,
  fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default function CreateChangeOrderModal({ projectId, onClose, onCreated, estimationType: propEstType }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<string>('MANUAL');
  const [sourceRef, setSourceRef] = useState('');
  const [storyPoints, setStoryPoints] = useState(0);
  const [euSize, setEuSize] = useState<string>('M');
  const [reviewUnits, setReviewUnits] = useState(1);
  const [estimatedDays, setEstimatedDays] = useState(0);
  const [costImpact, setCostImpact] = useState<string>('NONE');
  const [estType, setEstType] = useState<EstimationType>(propEstType ?? 'BOTH');

  useEffect(() => {
    if (propEstType) return;
    apiFetch<{ estimationType?: string }>(`/projects/${projectId}`)
      .then(p => {
        if (p.estimationType === 'STORY_POINTS' || p.estimationType === 'EU_RU' || p.estimationType === 'BOTH') {
          setEstType(p.estimationType);
        }
      })
      .catch(() => {});
  }, [projectId, propEstType]);
  const [options, setOptions] = useState<ChangeOrderOption[]>(DEFAULT_OPTIONS);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateOption = (idx: number, field: keyof ChangeOrderOption, value: string | boolean) => {
    setOptions(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  const submit = async (action: 'draft' | 'submit') => {
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const impact: Record<string, unknown> = { estimatedDays, costImpact, costNote: null };
      if (estType === 'STORY_POINTS' || estType === 'BOTH') impact.storyPoints = storyPoints;
      if (estType === 'EU_RU' || estType === 'BOTH') { impact.euSize = euSize; impact.reviewUnits = reviewUnits; }
      const body = {
        title, description, type,
        sourceRef: sourceRef || null,
        impact,
        options,
      };
      const result = await apiFetch<{ changeOrder: { id: string } }>(
        `/projects/${projectId}/change-orders`,
        { method: 'POST', body: JSON.stringify(body) },
      );

      if (action === 'submit') {
        await apiFetch(`/projects/${projectId}/change-orders/${result.changeOrder.id}/submit`, { method: 'POST' });
      }

      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create change order');
    } finally {
      setSaving(false);
    }
  };

  if (preview) {
    return (
      <SlideOver
        isOpen
        onClose={onClose}
        title="Client Preview"
        subtitle="This is what the client will see."
        width="lg"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setPreview(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
              ← Back to edit
            </button>
            <button onClick={() => void submit('submit')} disabled={saving} style={{ padding: '8px 16px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Submitting…' : 'Submit to Client →'}
            </button>
          </div>
        }
      >
          <div style={{ padding: 16, background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>{title || 'Untitled'}</h4>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 12 }}>{description}</p>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
              {(estType === 'STORY_POINTS' || estType === 'BOTH') && <span>{storyPoints} pts</span>}
              {(estType === 'EU_RU' || estType === 'BOTH') && <span>EU: {euSize} | RU: {reviewUnits}</span>}
              <span>{estimatedDays} days</span>
            </div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Options:</p>
            {options.map(o => (
              <div key={o.id} style={{ padding: '8px 12px', marginBottom: 4, background: 'var(--surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{o.label}</span>
                {o.recommended && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}>Recommended</span>}
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>{o.description}</p>
              </div>
            ))}
          </div>
      </SlideOver>
    );
  }

  return (
    <SlideOver
      isOpen
      onClose={onClose}
      title="New Change Order"
      subtitle="Create a change order to propose scope changes to the client."
      width="lg"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => void submit('draft')} disabled={saving} style={{ padding: '8px 16px', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
            Save as Draft
          </button>
          <button onClick={() => setPreview(true)} style={{ padding: '8px 16px', background: 'var(--surface-alt)', border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}>
            Preview →
          </button>
          <button onClick={() => void submit('submit')} disabled={saving} style={{ padding: '8px 16px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Submitting…' : 'Submit to Client →'}
          </button>
        </div>
      }
    >
        {error && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
            {error}
          </div>
        )}

        {/* Title */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Title (client-readable, no jargon)</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="e.g. Add OAuth login support" />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Plain-English explanation for the client…" />
        </div>

        {/* Type */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Type</label>
          <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
            {CO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Source ref */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Source reference (optional)</label>
          <input value={sourceRef} onChange={e => setSourceRef(e.target.value)} style={inputStyle} placeholder="PR #202 or US-7A.B3" />
        </div>

        {/* Impact */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Impact</label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(estType === 'STORY_POINTS' || estType === 'BOTH') && (
              <div style={{ flex: 1, minWidth: 100 }}>
                <label style={{ ...labelStyle, fontSize: 11 }}>Story points</label>
                <input type="number" min={0} value={storyPoints} onChange={e => setStoryPoints(Number(e.target.value))} style={inputStyle} />
              </div>
            )}
            {(estType === 'EU_RU' || estType === 'BOTH') && (
              <>
                <div style={{ flex: 1, minWidth: 100 }}>
                  <label style={{ ...labelStyle, fontSize: 11 }}>EU Size</label>
                  <select value={euSize} onChange={e => setEuSize(e.target.value)} style={inputStyle}>
                    <option value="S">S (~12 min)</option>
                    <option value="M">M (~25 min)</option>
                    <option value="L">L (~50 min)</option>
                    <option value="XL">XL (~90 min)</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 100 }}>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Review Units (RU)</label>
                  <select value={reviewUnits} onChange={e => setReviewUnits(Number(e.target.value))} style={inputStyle}>
                    <option value={1}>RU 1 (&lt;20 min)</option>
                    <option value={2}>RU 2 (20-45 min)</option>
                    <option value={3}>RU 3 (45-90 min)</option>
                  </select>
                </div>
              </>
            )}
            <div style={{ flex: 1, minWidth: 100 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>Est. days (0 = no impact)</label>
              <input type="number" min={0} value={estimatedDays} onChange={e => setEstimatedDays(Number(e.target.value))} style={inputStyle} />
            </div>
            <div style={{ flex: 1, minWidth: 100 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>Cost</label>
              <select value={costImpact} onChange={e => setCostImpact(e.target.value)} style={inputStyle}>
                {COST_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Options */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Options for client</label>
          {options.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <input value={o.label} onChange={e => updateOption(i, 'label', e.target.value)} placeholder="Label" style={{ ...inputStyle, flex: 1 }} />
              <input value={o.description} onChange={e => updateOption(i, 'description', e.target.value)} placeholder="Description" style={{ ...inputStyle, flex: 2 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={o.recommended} onChange={e => updateOption(i, 'recommended', e.target.checked)} />
                Rec.
              </label>
            </div>
          ))}
          <button type="button" onClick={() => setOptions(prev => [...prev, { id: `opt-${Date.now()}`, label: '', description: '', recommended: false }])} style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
            + Add option
          </button>
        </div>
    </SlideOver>
  );
}
